import { useState } from "react";
import { Loader2, Save, Lock } from "lucide-react";
import { apiPut } from "@/lib/api";
import { SYMBOL_LIST } from "@/lib/symbols";

export const SettingsPanel = ({ settings, onSaved, broker, activeSymbols }) => {
  const [mode, setMode] = useState(settings?.mode || "analysis");
  const [robotOn, setRobotOn] = useState(settings?.robot_on || false);
  const [minScore, setMinScore] = useState(settings?.min_score ?? 70);
  const [risk, setRisk] = useState(settings?.risk_pct ?? 1);
  const [watch, setWatch] = useState(settings?.watch_symbols?.length ? settings.watch_symbols : SYMBOL_LIST);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const toggleWatch = (s) =>
    setWatch((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await apiPut("/settings", { mode, robot_on: mode === "paper_auto" ? robotOn : false, min_score: minScore, risk_pct: risk, watch_symbols: watch });
      setSaved(true);
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl border border-white/10 bg-ink-surface p-6" data-testid="settings-panel">
      <h3 className="font-display text-lg font-bold text-white">Mode Sistem</h3>
      <div className="mt-4 grid gap-2 sm:grid-cols-3">
        {[
          ["analysis", "Analysis Only", "Sinyal & data saja, tanpa eksekusi", true],
          ["paper_auto", "Paper Auto", "Fitur auto trading simulasi sedang dikunci", false],
          ["live",
            broker?.account_type === "demo" ? "Demo Auto (Broker)" : "Live Trading (Real)",
            false
              ? "Fitur eksekusi broker sedang dikunci"
              : "Fitur eksekusi broker sedang dikunci",
            false],
        ].map(([key, label, desc, enabled]) => (
          <button
            key={key}
            disabled={!enabled}
            onClick={() => { setMode(key); if (key !== "paper_auto") setRobotOn(false); }}
            data-testid={`settings-mode-${key}`}
            className={`border p-4 text-left transition-colors duration-200 ${
              !enabled
                ? "cursor-not-allowed border-sell/30 bg-sell/5 opacity-70"
                : mode === key
                ? "border-buy bg-buy/10"
                : "border-white/15 hover:border-white/40"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className={`font-mono text-xs font-bold ${!enabled ? "text-sell" : mode === key ? "text-buy" : "text-white"}`}>
                {label}
              </span>
              {!enabled && <Lock size={12} className="text-sell" />}
            </div>
            <p className="mt-2 text-[11px] leading-relaxed text-mute">{desc}</p>
          </button>
        ))}
      </div>

      {mode === "paper_auto" && (
        <div
          data-testid="settings-robot-toggle-locked"
          className="mt-4 flex w-full items-center justify-between border border-sell/30 bg-sell/5 px-4 py-3 font-mono text-xs font-bold text-sell"
        >
          ROBOT PAPER TRADING
          <span className="flex items-center gap-2 bg-sell/10 px-2 py-0.5">
            <Lock size={12} />
            LOCKED
          </span>
        </div>
      )}

      <div className="mt-6">
        <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-mute-dim">
          Simbol yang dianalisis & dikirim ke Telegram
        </p>
        <div className="flex flex-wrap gap-1.5">
          {(activeSymbols?.length ? activeSymbols : SYMBOL_LIST).map((s) => (
            <button key={s} type="button" onClick={() => toggleWatch(s)} data-testid={`watch-symbol-${s.toLowerCase()}`}
              className={`border px-3 py-2 font-mono text-xs font-bold transition-colors duration-150 ${
                watch.includes(s) ? "border-buy bg-buy/15 text-buy" : "border-white/15 text-mute-dim"}`}>
              {s}
            </button>
          ))}
        </div>
        <p className="mt-2 font-mono text-[9px] text-mute-dim">
          &gt; scanner, notifikasi telegram, dan robot paper auto hanya fokus ke simbol yang aktif ({watch.length || 0} dipilih)
        </p>
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-mute-dim">
            Skor minimum entry: <b className="text-white">{minScore}</b>
          </p>
          <input type="range" min="60" max="90" step="5" value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))}
            data-testid="settings-minscore-range" className="w-full accent-[#00E676]" />
        </div>
        <div>
          <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-mute-dim">Risiko per trade</p>
          <select value={risk} onChange={(e) => setRisk(Number(e.target.value))} data-testid="settings-risk-select"
            className="h-10 w-full border border-white/15 bg-ink px-3 font-mono text-sm text-white">
            {[0.5, 1, 1.5, 2, 3].map((r) => <option key={r} value={r}>{r}% dari saldo</option>)}
          </select>
        </div>
      </div>

      <div className="mt-6 border border-white/10 bg-ink p-4 font-mono text-[10px] leading-relaxed text-mute-dim">
        &gt; batas otomatis: maks 5 trade/hari · batas rugi harian 3% · stop setelah 3 rugi beruntun ·
        SL/TP wajib pada setiap posisi
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-save-button"
        className="mt-6 flex h-11 items-center gap-2 border border-buy bg-buy/10 px-6 font-mono text-sm font-bold uppercase tracking-wider text-buy transition-colors duration-200 hover:bg-buy hover:text-ink disabled:opacity-60">
        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
        {saved ? "Tersimpan ✓" : "Simpan Pengaturan"}
      </button>
    </div>
  );
};
